/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\packages\operator-admin\src\router\index.ts
 * 路由配置
 */

import { createRouter, createWebHistory } from 'vue-router'
import type { RouteRecordRaw } from 'vue-router'
import { setupAuthGuards } from '@shared/utils/route-guards'
import { setupChunkErrorHandler } from '@shared/utils/chunk-error-handler'

// 路由配置
const routes: RouteRecordRaw[] = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/auth/LoginView.vue'),
    meta: {
      title: '登录',
      requiresAuth: false
    }
  },
  {
    path: '/',
    name: 'Layout',
    component: () => import('../layouts/MainLayout.vue'),
    redirect: { name: 'Dashboard' },
    meta: {
      requiresAuth: true,
      // 运营商后台应用级放行：允许运营商体系角色访问（管理员及各部门/区域员工）；
      // 页面与按钮权限、数据范围（部门/城市/区域等）在下游按权限码与范围策略细化
      roles: ['operator']
    },
    children: [
      {
        path: 'dashboard',
        name: 'Dashboard',
        component: () => import('../views/dashboard/DashboardView.vue'),
        meta: {
          title: '实时指挥中心',
          icon: 'Monitor',
          requiresAuth: true,
          // 直链最小权限：dashboard:view
          permissions: ['dashboard:view']
        }
      },
      // 🔧 调试页面（开发用）
      {
        path: 'debug/permissions',
        name: 'PermissionDebug',
        component: () => import('../pages/debug/PermissionDebugPage.vue'),
        meta: {
          title: '权限调试',
          requiresAuth: true
        }
      },
      // 订单中心扩展：线上/到店/职场
      { path: 'orders/online', name: 'OrderOnlineList', component: () => import('../views/orders/OnlineOrderListView.vue'), meta: { title: '线上订单', requiresAuth: true, permissions: ['order:online:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '订单中心' }, { title: '订单管理', path: '/order-center/manage' }, { title: '线上订单' }] } },
      { path: 'orders/in-store', name: 'OrderInStoreList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '到店订单', requiresAuth: true, permissions: ['order:in_store:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '订单中心' }, { title: '订单管理', path: '/order-center/manage' }, { title: '到店订单' }] } },
      { path: 'orders/workplace', name: 'OrderWorkplaceList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '职场打餐', requiresAuth: true, permissions: ['order:workplace:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '订单中心' }, { title: '订单管理', path: '/order-center/manage' }, { title: '职场打餐' }] } },
      // —— 企业管理（重构项） ——
      { path: 'enterprises/info', name: 'EnterpriseInfo', component: () => import('../views/enterprises/EnterpriseListView.vue'), meta: { title: '企业信息', requiresAuth: true, permissions: ['enterprises:enterprise:list', 'enterprise:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '企业信息' }] } },
      { path: 'enterprises/detail/:id', name: 'EnterpriseDetail', component: () => import('../views/enterprises/EnterpriseDetailView.vue'), meta: { title: '企业详情', requiresAuth: true, permissions: ['enterprises:enterprise:read', 'enterprise:view'], menuId: 'EnterpriseInfo', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '企业信息', path: '/enterprises/info' }, { title: '企业详情' }] } },
      { path: 'enterprises/create', name: 'EnterpriseCreate', component: () => import('../views/enterprises/EnterpriseFormView.vue'), meta: { title: '新建企业', requiresAuth: true, permissions: ['enterprises:enterprise:create', 'enterprise:create'], menuId: 'EnterpriseInfo', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '企业信息', path: '/enterprises/info' }, { title: '新建企业' }] } },
      { path: 'enterprises/edit/:id', name: 'EnterpriseEdit', component: () => import('../views/enterprises/EnterpriseFormView.vue'), meta: { title: '编辑企业', requiresAuth: true, permissions: ['enterprises:enterprise:update', 'enterprise:edit'], menuId: 'EnterpriseInfo', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '企业信息', path: '/enterprises/info' }, { title: '编辑企业' }] } },
      // [已清理] 订单调整相关路由已移至"订单中心 > 订单调整与取餐"（/order-center/adjustment）
      // 统一使用"排餐管理"作为入口，承载排期管理功能
      { path: 'enterprises/service-relations', name: 'MenuScheduleList', component: () => import('../pages/menu-schedule/MenuScheduleListPage.vue'), meta: { title: '排餐管理', requiresAuth: true, permissions: ['menu_schedule:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '排餐管理' }] } },
      { path: 'enterprises/recharges', name: 'EnterpriseRecharges', component: () => import('../pages/finance/FinanceManagementPage.vue'), meta: { title: '充值管理', requiresAuth: true, permissions: ['enterprise_recharge:list', 'subsidy_transfer:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '充值管理' }] } },
      {
        path: 'enterprises/restaurant-recommendation',
        name: 'RestaurantRecommendation',
        component: () => import('../views/enterprises/RestaurantRecommendationView.vue'),
        meta: {
          title: '餐厅推荐设置',
          requiresAuth: true,
          // 对齐后端权限：enterprise_restaurant_recommendation:list
          permissions: ['enterprise_restaurant_recommendation:list'],
          recommendationMode: 'enterprise',
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '餐厅推荐设置' }],
        },
      },
      {
        path: 'system/restaurant-recommendation',
        name: 'SystemRestaurantRecommendation',
        component: () => import('../views/enterprises/RestaurantRecommendationView.vue'),
        meta: {
          title: '标准推荐设置',
          requiresAuth: true,
          permissions: ['enterprise_restaurant_recommendation:list'],
          recommendationMode: 'system',
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '系统与配置' }, { title: '标准推荐设置' }],
        },
      },
      // MC-6b: MC绑定管理（Mode B-MC 企业员工与MC账号绑定）
      {
        path: 'enterprises/meican-bindings',
        name: 'MeicanBindingManagement',
        component: () => import('../views/enterprises/MeicanBindingView.vue'),
        meta: {
          title: 'MC绑定管理',
          requiresAuth: true,
          permissions: ['enterprises:enterprise:list', 'enterprise:list'],
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: 'MC绑定管理' }]
        }
      },
      // —— 排期管理（细分页面：详情/编辑），面包屑回到"排餐管理" ——
      { path: 'enterprises/menu-schedules/:id', name: 'MenuScheduleDetail', component: () => import('../pages/menu-schedule/MenuScheduleDetailPage.vue'), meta: { title: '排期详情', requiresAuth: true, permissions: ['menu_schedule:view'], menuId: 'MenuScheduleList', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '排餐管理', path: '/enterprises/service-relations' }, { title: '排期详情' }] } },
      { path: 'enterprises/menu-schedules/edit', name: 'MenuScheduleEdit', component: () => import('../pages/menu-schedule/MenuScheduleEditPage.vue'), meta: { title: '编辑菜单', requiresAuth: true, permissions: ['menu_schedule:edit'], menuId: 'MenuScheduleList', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '排餐管理', path: '/enterprises/service-relations' }, { title: '编辑菜单' }] } },
      // —— 商户管理（重构项） ——
      { path: 'merchants/info', name: 'MerchantInfo', component: () => import('../pages/merchants/MerchantListPage.vue'), meta: { title: '商户信息', requiresAuth: true, permissions: ['merchant:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '商户信息' }] } },
      { path: 'merchants/create', name: 'MerchantCreate', component: () => import('../pages/merchants/MerchantCreatePage.vue'), meta: { title: '新增商户', requiresAuth: true, permissions: ['merchant:create'], menuId: 'MerchantInfo', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '商户信息', path: '/merchants/info' }, { title: '新增商户' }] } },
      { path: 'merchants/:id', name: 'MerchantDetail', component: () => import('../pages/merchants/MerchantDetailPage.vue'), meta: { title: '商户详情', requiresAuth: true, permissions: ['merchant:view'], menuId: 'MerchantInfo', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '商户信息', path: '/merchants/info' }, { title: '商户详情' }] } },
      { path: 'merchants/:id/edit', name: 'MerchantEdit', component: () => import('../pages/merchants/MerchantEditPage.vue'), meta: { title: '编辑商户', requiresAuth: true, permissions: ['merchant:edit'], menuId: 'MerchantInfo', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '商户信息', path: '/merchants/info' }, { title: '编辑商户' }] } },
      // [已清理] 排期与菜单路由已在"企业管理 > 排餐管理"和商户端"我的排期"实现
      { path: 'merchants/development', name: 'MerchantDevelopment', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '需求与开发', requiresAuth: true, permissions: ['merchant_development:list'] } },
      { path: 'merchants/order-stats', name: 'MerchantOrderStats', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '订单数据', requiresAuth: true, permissions: ['merchant_order:list'] } },
      { path: 'merchants/order-stats/preorder', name: 'MerchantPreorderStats', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '预定订单', requiresAuth: true, permissions: ['merchant_order:list'] } },
      { path: 'merchants/order-stats/today', name: 'MerchantTodayOrderStats', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '今日订单', requiresAuth: true, permissions: ['merchant_order:list'] } },
      {
        path: 'tenants',
        name: 'Tenants',
        component: () => import('../views/tenants/TenantListView.vue'),
        meta: {
          title: '租户管理',
          icon: 'OfficeBuilding',
          requiresAuth: true,
          permissions: ['tenant:list']
        }
      },
      // 目录管理扩展：平台能力、服务类型、餐段类别
      { path: 'catalogs/platform-capabilities', name: 'PlatformCapabilityList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '平台能力目录', requiresAuth: true, permissions: ['platform_capability:list'] } },
      { path: 'catalogs/service-types', name: 'ServiceTypeList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '服务类型目录', requiresAuth: true, permissions: ['service_type:list'] } },
      { path: 'catalogs/meal-segment-types', name: 'MealSegmentTypeList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '餐段类别目录', requiresAuth: true, permissions: ['meal_segment_type:list'] } },
      { // 详情
        path: 'tenants/detail/:id',
        name: 'TenantDetail',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '租户详情', requiresAuth: true, permissions: ['tenant:view'] }
      },
      { // 数据访问配置
        path: 'tenants/access-config',
        name: 'TenantAccessConfig',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '数据访问配置', requiresAuth: true, permissions: ['tenant_access_config:list'] }
      },
      { // 租户审计日志
        path: 'tenants/audit',
        name: 'TenantAudit',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '租户审计日志', requiresAuth: true, permissions: ['tenant_audit:list'] }
      },
      {
        path: 'catalogs',
        name: 'Catalogs',
        redirect: '/catalogs/brands',
        meta: {
          title: '目录管理',
          icon: 'FolderOpened',
          requiresAuth: true
        },
        children: [
          {
            path: 'brands',
            name: 'Brands',
            component: () => import('../views/catalogs/BrandListView.vue'),
            meta: {
              title: '品牌管理',
              requiresAuth: true,
              permissions: ['brand:list']
            }
          },
          {
            path: 'cuisines',
            name: 'Cuisines',
            component: () => import('../views/catalogs/CuisineListView.vue'),
            meta: {
              title: '菜系管理',
              requiresAuth: true,
              permissions: ['cuisine:list']
            }
          },
          {
            path: 'dish-categories',
            name: 'DishCategories',
            component: () => import('../views/catalogs/DishCategoryListView.vue'),
            meta: {
              title: '菜品分类',
              requiresAuth: true,
              permissions: ['dish_category:list']
            }
          }
        ]
      },
      {
        path: 'merchants',
        name: 'Merchants',
        component: () => import('../views/merchants/MerchantListView.vue'),
        meta: {
          title: '商户管理',
          icon: 'Shop',
          requiresAuth: true,
          permissions: ['merchant:list']
        }
      },
      { // 门店（餐厅）
        path: 'merchants/restaurants',
        name: 'RestaurantList',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '门店列表', requiresAuth: true, permissions: ['restaurant:list'] }
      },
      { // 菜品库（简约版）
        path: 'merchants/dishes',
        name: 'DishLibrary',
        component: () => import('../pages/merchants/DishLibraryPage.vue'),
        meta: { title: '菜品库', requiresAuth: true, permissions: ['dish:list'] }
      },
      { // 新建菜品
        path: 'merchants/dishes/create',
        name: 'DishCreate',
        component: () => import('../pages/merchants/DishFormPage.vue'),
        meta: { title: '新建菜品', requiresAuth: true, permissions: ['dish:create'], menuId: 'DishLibrary', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '菜品库', path: '/merchants/dishes' }, { title: '新建菜品' }] }
      },
      { // 设置菜品标签（已实现）
        path: 'catalogs/dish-tags',
        name: 'DishTagSettings',
        component: () => import('../pages/merchants/DishTagSettingsPage.vue'),
        meta: { title: '菜品标签', requiresAuth: true, permissions: ['dish_tag:list'], menuId: 'DishLibrary', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '菜品库', path: '/merchants/dishes' }, { title: '菜品标签' }] }
      },
      { // 菜品详情
        path: 'merchants/dishes/:id',
        name: 'DishDetail',
        // ⚠️ 详情抽屉是 Teleport 组件（el-drawer）。若作为独立路由页渲染，RouterView 的 Transition 可能在卸载时触发 parentNode=null 崩溃，导致返回列表白屏。
        // 解决：保持在“列表页”内渲染抽屉，通过 query.detail 控制显示，详情路由仅作为深链接入口重定向到列表。
        redirect: (to) => ({
          name: 'DishLibrary',
          query: { ...(to.query || {}), detail: String(to.params.id || '') }
        }),
        meta: { title: '菜品详情', requiresAuth: true, permissions: ['dish:view'], menuId: 'DishLibrary', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '菜品库', path: '/merchants/dishes' }, { title: '菜品详情' }] }
      },
      { // 编辑菜品
        path: 'merchants/dishes/:id/edit',
        name: 'DishEdit',
        component: () => import('../pages/merchants/DishFormPage.vue'),
        meta: { title: '编辑菜品', requiresAuth: true, permissions: ['dish:edit'], menuId: 'DishLibrary', breadcrumbs: [{ title: '首页', path: '/' }, { title: '商户管理' }, { title: '菜品库', path: '/merchants/dishes' }, { title: '编辑菜品' }] }
      },
      { // 菜单与上下架
        path: 'merchants/menu',
        name: 'MerchantMenu',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '菜单与上下架', requiresAuth: true, permissions: ['merchant_menu:list'] }
      },
      { // 资质与合同映射
        path: 'merchants/compliance',
        name: 'MerchantCompliance',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '资质与合同映射', requiresAuth: true, permissions: ['merchant_compliance:list'] }
      },
      {
        path: 'enterprises',
        name: 'Enterprises',
        component: () => import('../views/enterprises/EnterpriseListView.vue'),
        meta: {
          title: '企业管理',
          icon: 'Building',
          requiresAuth: true,
          permissions: ['enterprise:list']
        }
      }
      ,
      { // 员工管理
        path: 'enterprises/employees',
        name: 'EnterpriseEmployees',
        component: () => import('../pages/employees/EmployeeListPage.vue'),
        meta: { title: '员工管理', requiresAuth: true, permissions: ['employee:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '员工管理' }] }
      },
      {
        path: 'enterprises/employees/nfc-cards',
        name: 'EnterpriseEmployeeNfcCards',
        component: () => import('../pages/employees/NfcCredentialPage.vue'),
        meta: {
          title: '员工 NFC 卡',
          requiresAuth: true,
          permissions: ['employee_nfc_binding:manage'],
          menuId: 'EnterpriseEmployees',
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '员工管理', path: '/enterprises/employees' }, { title: '员工 NFC 卡' }]
        }
      },
      { // Phase 5: 打卡记录
        path: 'enterprises/checkin-records',
        name: 'CheckinRecords',
        component: () => import('../views/enterprises/CheckinRecordListView.vue'),
        meta: { title: '打卡记录', requiresAuth: true, permissions: ['enterprises:enterprise:list', 'enterprise:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '打卡记录' }] }
      },
      {
        path: 'enterprises/external-attendance',
        name: 'ExternalAttendance',
        component: () => import('../views/enterprises/ExternalAttendanceListView.vue'),
        meta: { title: '外部考勤记录', requiresAuth: true, permissions: ['enterprises:enterprise:list', 'enterprise:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '外部考勤记录' }] }
      },
      {
        path: 'enterprises/attendance-rules',
        name: 'AttendanceRuleOverview',
        component: () => import('../views/enterprises/AttendanceRuleOverviewView.vue'),
        meta: { title: '考勤规则总览', requiresAuth: true, permissions: ['enterprises:enterprise:list', 'enterprise:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '考勤规则总览' }] }
      },
      { // 员工钱包详情（新增 2025-11-16 - 跨端复用 enterprise-admin）
        path: 'enterprises/employees/:id/wallet',
        name: 'EmployeeWallet',
        component: () => import('../pages/employees/EmployeeWalletPage.vue'),
        meta: { title: '员工钱包', requiresAuth: true, permissions: ['employee:view'], menuId: 'EnterpriseEmployees', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '员工管理', path: '/enterprises/employees' }, { title: '员工钱包' }] }
      },
      { // 新增员工
        path: 'enterprises/employees/create',
        name: 'EmployeeCreate',
        redirect: { name: 'EnterpriseEmployees' },
        meta: { title: '新增员工', requiresAuth: true, permissions: ['employee:create'], menuId: 'EnterpriseEmployees', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '员工管理', path: '/enterprises/employees' }, { title: '新增员工' }] }
      },
      { // 编辑员工
        path: 'enterprises/employees/edit/:id',
        name: 'EmployeeEdit',
        redirect: { name: 'EnterpriseEmployees' },
        meta: { title: '编辑员工', requiresAuth: true, permissions: ['employee:edit'], menuId: 'EnterpriseEmployees', breadcrumbs: [{ title: '首页', path: '/' }, { title: '企业管理' }, { title: '员工管理', path: '/enterprises/employees' }, { title: '编辑员工' }] }
      },
      // IAM 用户与权限 - 统一入口（Tab 布局）
      {
        path: 'iam',
        name: 'IAMManagement',
        component: () => import('../pages/users/IAMManagementPage.vue'),
        meta: { title: '用户与权限', requiresAuth: true, permissions: ['user:list'] }
      },
      // IAM 子路由重定向到主页面（带 tab 参数）
      {
        path: 'iam/users',
        name: 'UserList',
        redirect: { name: 'IAMManagement', query: { tab: 'users' } },
        meta: { title: '用户管理', requiresAuth: true, permissions: ['user:list'] }
      },
      {
        path: 'iam/roles',
        name: 'RoleList',
        redirect: { name: 'IAMManagement', query: { tab: 'roles' } },
        meta: { title: '角色管理', requiresAuth: true, permissions: ['role:list'] }
      },
      {
        path: 'iam/permissions',
        name: 'PermissionMatrix',
        redirect: { name: 'IAMManagement', query: { tab: 'permissions' } },
        meta: { title: '权限矩阵', requiresAuth: true, permissions: ['permission:list'] }
      },
      {
        path: 'iam/whitelist',
        name: 'AccessWhitelist',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '特批白名单', requiresAuth: true, permissions: ['access_whitelist:list'] }
      },
      {
        path: 'iam/audit',
        name: 'IamAudit',
        component: () => import('../views/common/PlaceholderView.vue'),
        meta: { title: '登录/操作审计', requiresAuth: true, permissions: ['iam_audit:list'] }
      },
      // 风控中心
      {
        path: 'security/fraud-alerts',
        name: 'FraudAlerts',
        component: () => import('../views/security/FraudAlertsView.vue'),
        meta: { title: '反欺诈告警', requiresAuth: true, permissions: ['security:fraud_alert:list'] }
      },
      {
        path: 'security/device-logs',
        name: 'DeviceLoginLogs',
        component: () => import('../views/security/DeviceLoginLogsView.vue'),
        meta: { title: '设备登录日志', requiresAuth: true, permissions: ['security:device_log:list'] }
      },
      // 订单中心
      { path: 'orders/list', name: 'OrderList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '订单列表', requiresAuth: true, permissions: ['order:list'] } },
      { path: 'orders/detail/:id', name: 'OrderDetail', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '订单详情', requiresAuth: true, permissions: ['order:view'] } },
      { path: 'orders/adjustments', name: 'OrderAdjustments', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '订单调整', requiresAuth: true, permissions: ['order_adjustment:list'] } },
      { path: 'orders/transfers', name: 'OrderTransfers', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '订单转单', requiresAuth: true, permissions: ['order_transfer:list'] } },
      { path: 'orders/exceptions', name: 'OrderExceptions', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '异常订单归档', requiresAuth: true, permissions: ['order_exception:list'] } },
      // —— 订单中心（重构域） ——
      { path: 'order-center/center', name: 'OrderCenter', component: () => import('../views/orders/center/OrderCenterView.vue'), meta: { title: '预订单管理', requiresAuth: true, permissions: ['order:online:list'], breadcrumbs: [{ title: '首页', path: '/' }, { title: '订单中心' }, { title: '预订单管理' }] } },
      { path: 'order-center/manage', name: 'OrderManage', component: () => import('../views/orders/OrderManagementView.vue'), meta: { title: '订单管理', requiresAuth: true, permissions: ['order:online:list'] } },
      { path: 'order-center/consumption', name: 'ConsumptionDetails', component: () => import('../views/orders/ConsumptionDetailsView.vue'), meta: { title: '消费明细', requiresAuth: true, permissions: ['consumption_detail:list'] } },
      { path: 'order-center/adjustment', name: 'OrderAdjustment', component: () => import('../views/orders/OrderAdjustmentView.vue'), meta: { title: '订单调整', requiresAuth: true, permissions: ['order_adjustment:list'] } },
      // 财务中心（重构 2026-04-06 Phase 2：对账→结算→开票→收付→报表 闭环）
      { path: 'finance/settlement-center', name: 'SettlementCenter', component: () => import('../views/finance/SettlementCenterView.vue'), meta: { title: '结算与对账', requiresAuth: true, permissions: ['settlement:list', 'finance_report:list'] } },
      { path: 'finance/commission-compensation', name: 'CommissionCompensation', component: () => import('../views/finance/CommissionCompensationView.vue'), meta: { title: '费用与规则', requiresAuth: true, permissions: ['commission_rule:list', 'sla_compensation:list', 'prepayment_advance:list', 'settlement_service_fee_rule:list'] } },
      { path: 'finance/assets-settlement', name: 'AssetsSettlement', component: () => import('../views/finance/AssetsSettlementView.vue'), meta: { title: '资产管理', requiresAuth: true, permissions: ['asset_inventory:list'] } },
      { path: 'finance/capital', name: 'CapitalManagement', component: () => import('../views/finance/CapitalManagementView.vue'), meta: { title: '资金与报表', requiresAuth: true, permissions: ['finance_cashflow:list', 'finance_report:list', 'subsidy_transfer:list'] } },
      // 旧路由重定向
      { path: 'finance/settlements', redirect: '/finance/settlement-center' },
      { path: 'finance/commission-rules', redirect: '/finance/commission-compensation' },
      { path: 'finance/reconciliation', redirect: '/finance/settlement-center?tab=ar' },
      // 合同与资质
      { path: 'legal/contracts', name: 'LegalContracts', component: () => import('../pages/legal/ContractListPage.vue'), meta: { title: '合同档案', requiresAuth: true, permissions: ['legal_contract:list'] } },
      { path: 'legal/qualifications', name: 'LegalQualifications', component: () => import('../pages/legal/QualificationListPage.vue'), meta: { title: '资质档案', requiresAuth: true, permissions: ['legal_qualification:list'] } },
      { path: 'legal/templates', name: 'LegalTemplates', component: () => import('../pages/legal/TemplatePage.vue'), meta: { title: '模板与范本', requiresAuth: true, permissions: ['legal_template:list'] } },
      { path: 'legal/parsing-tasks', name: 'LegalParsingTasks', component: () => import('../pages/legal/ParsingTaskPage.vue'), meta: { title: '解析任务与OCR', requiresAuth: true, permissions: ['legal_parsing_task:list'] } },
      // 硬件管理
      { path: 'hardware/devices', name: 'DeviceList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '设备列表', requiresAuth: true, permissions: ['hardware_device:list'] } },
      { path: 'hardware/cabinet-allocation', name: 'CabinetAllocation', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '柜体分配', requiresAuth: true, permissions: ['cabinet_allocation:list'] } },
      // —— 软硬件管理（重构域） ——
      { path: 'hardware/smart-cabinets', name: 'SmartCabinets', component: () => import('../views/hardware/SmartCabinetsView.vue'), meta: { title: '智能餐柜', requiresAuth: true, permissions: ['hardware_smart_cabinet:list'] } },
      { path: 'hardware/card-readers', name: 'CardReaders', component: () => import('../views/hardware/CardReadersView.vue'), meta: { title: '嘀卡机', requiresAuth: true, permissions: ['hardware_card_reader:list', 'hardware_smart_cabinet:list'] } },
      { path: 'hardware/snack-cabinets', name: 'SnackCabinets', component: () => import('../views/hardware/SnackCabinetsView.vue'), meta: { title: '智能零食柜', requiresAuth: true, permissions: ['hardware_snack_cabinet:list'] } },
      { path: 'hardware/cloud-printers', name: 'CloudPrinters', component: () => import('../views/hardware/CloudPrintersView.vue'), meta: { title: '云打印机', requiresAuth: true, permissions: ['hardware_smart_cabinet:list'] } },
      { path: 'hardware/online-mall', name: 'OnlineMall', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '线上商城', requiresAuth: true, permissions: ['online_mall:list'] } },
      // 巡检与稽核
      { path: 'inspections/tasks', name: 'InspectionTaskList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '巡检任务', requiresAuth: true, permissions: ['inspection_task:list'] } },
      { path: 'inspections/results', name: 'InspectionResultList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '结果与整改', requiresAuth: true, permissions: ['inspection_result:list'] } },
      // —— 巡检与稽核（重构域） ——
      { path: 'inspections/customer', name: 'CustomerInspections', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '客户巡检', requiresAuth: true, permissions: ['inspection_customer:list'] } },
      { path: 'inspections/merchant', name: 'MerchantInspections', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '商户巡检', requiresAuth: true, permissions: ['inspection_merchant:list'] } },
      { path: 'inspections/rectifications', name: 'RectificationReports', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '整改报告', requiresAuth: true, permissions: ['inspection_rectification_report:list'] } },
      // 工单与客服（重构）
      { path: 'tickets-support/tickets', name: 'TicketManagement', component: () => import('../views/tickets/TicketManagementView.vue'), meta: { title: '工单处理', requiresAuth: true, permissions: ['ticket:list'] } },
      { path: 'tickets-support/reviews', name: 'ReviewManagement', component: () => import('../views/tickets/ReviewManagementView.vue'), meta: { title: '评价与客诉', requiresAuth: true, permissions: ['review:list'] } },
      { path: 'tickets-support/chat', name: 'ChatWorkstation', component: () => import('../views/tickets/ChatWorkstationView.vue'), meta: { title: '在线客服', menuId: 'tickets-support', requiresAuth: true } },
      { path: 'tickets-support/statistics', name: 'ServiceStatistics', component: () => import('../views/tickets/ServiceStatisticsView.vue'), meta: { title: '客服统计', requiresAuth: true, permissions: ['ticket_stats:view'] } },
      { path: 'tickets-support/agent-config', name: 'AgentConfig', component: () => import('../views/tickets/AgentConfigView.vue'), meta: { title: '坐席与分组', menuId: 'tickets-support', requiresAuth: true } },
      { path: 'tickets-support/quick-replies', name: 'QuickReplyManagement', component: () => import('../views/tickets/QuickReplyManagementView.vue'), meta: { title: '快捷回复管理', menuId: 'tickets-support', requiresAuth: true } },
      // 旧路由重定向
      { path: 'tickets/list', redirect: '/tickets-support/tickets' },
      { path: 'tickets/stats', redirect: '/tickets-support/statistics' },
      { path: 'tickets-support/agents', redirect: '/tickets-support/agent-config?tab=agents' },
      { path: 'tickets-support/agent-groups', redirect: '/tickets-support/agent-config?tab=groups' },
      // 供应链管理
      { path: 'supply-chain/overview', name: 'SupplyChainOverview', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '供应链概览', requiresAuth: true, permissions: ['supply_chain:view'] } },
      { path: 'supply-chain/records', name: 'SupplyChainRecords', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '采购/配送记录', requiresAuth: true, permissions: ['supply_chain_record:list'] } },
      { path: 'supply-chain/purchases', name: 'PurchaseRecords', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '采购记录', requiresAuth: true, permissions: ['purchase_record:list'] } },
      // 评价与口碑
      { path: 'reviews/list', name: 'ReviewList', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '评价列表', requiresAuth: true, permissions: ['review:list'] } },
      { path: 'reviews/moderation', name: 'ReviewModeration', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '敏感词与申诉', requiresAuth: true, permissions: ['review_moderation:list'] } },
      // 系统配置
      { path: 'system/region-permission', name: 'RegionPermissionConfig', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '地区与权限配置', requiresAuth: true, permissions: ['region_permission_config:list'] } },
      { path: 'system/approval-workflow', name: 'ApprovalWorkflow', component: () => import('../views/common/PlaceholderView.vue'), meta: { title: '审批工作流', requiresAuth: true, permissions: ['approval_workflow:list'] } },
      {
        path: 'system/open-api',
        name: 'OpenApiManagement',
        component: () => import('../views/system/OpenApiManagementView.vue'),
        meta: { title: 'Open API 管理', requiresAuth: true }
      },
      { 
        path: 'system/label-templates', 
        name: 'LabelTemplateConfig', 
        component: () => import('../views/system/LabelTemplateConfigView.vue'), 
        meta: { 
          title: '标签模板配置', 
          requiresAuth: true, 
          // permissions: ['print_template:list'],  // 暂不校验，后端 sync_permissions 后启用
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '系统与配置' }, { title: '标签模板配置' }]
        } 
      },
      // —— 系统-枚举目录（重构：统一常量管理页面） ——
      { 
        path: 'system/catalogs', 
        name: 'CatalogManagement', 
        component: () => import('../views/catalogs/CatalogManagementView.vue'), 
        meta: { 
          title: '常量目录管理', 
          requiresAuth: true, 
          permissions: ['brand:list'],
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '系统与配置' }, { title: '常量目录管理' }]
        } 
      },
      // 保留旧路由作为备用入口（重定向到统一页面）
      { path: 'system/catalogs/brands', redirect: '/system/catalogs', name: 'BrandList' },
      { path: 'system/catalogs/platform-capabilities', redirect: '/system/catalogs', name: 'PlatformCapabilityList' },
      { path: 'system/catalogs/service-types', redirect: '/system/catalogs', name: 'ServiceTypeList' },
      { path: 'system/catalogs/meal-segment-types', redirect: '/system/catalogs', name: 'MealSegmentTypeList' },
      { path: 'system/catalogs/cuisines', redirect: '/system/catalogs', name: 'CuisineList' },
      { path: 'system/catalogs/dish-categories', redirect: '/system/catalogs', name: 'DishCategoryList' },
      // —— 关于美膳 ——
      { path: 'about/org-staff', name: 'AboutOrgStaff', redirect: to => ({ name: 'AboutOrganization', query: { ...to.query, tab: 'staff' }, hash: to.hash }) },
      { path: 'about/positions', name: 'AboutPositions', redirect: to => ({ name: 'AboutOrganization', query: { ...to.query, tab: 'positions', positionTab: 'positions' }, hash: to.hash }) },
      { path: 'about/position-changes', name: 'AboutPositionChanges', redirect: to => ({ name: 'AboutOrganization', query: { ...to.query, tab: 'positions', positionTab: 'approvals' }, hash: to.hash }) },
      { path: 'about/operator-cities', name: 'AboutOperatorCities', redirect: to => ({ name: 'AboutGeography', query: { ...to.query, tab: 'cities' }, hash: to.hash }) },
      { path: 'about/operator-regions', name: 'AboutOperatorRegions', redirect: to => ({ name: 'AboutGeography', query: { ...to.query, tab: 'regions' }, hash: to.hash }) },
      { path: 'about/role-permission-codes', name: 'AboutRolePermissionCodes', redirect: to => ({ name: 'AboutPermissions', query: { ...to.query, tab: 'roles' }, hash: to.hash }) },
      { path: 'about/data-scope', name: 'AboutDataScope', redirect: to => ({ name: 'AboutPermissions', query: { ...to.query, tab: 'scope' }, hash: to.hash }) },
      { path: 'about/organization', name: 'AboutOrganization', component: () => import('../pages/about/AboutManagementPage.vue'), props: { section: 'organization' }, meta: { title: '组织与人员管理', requiresAuth: true, permissions: ['operator_org_staff:list', 'data_scope_config:list'] } },
      { path: 'about/geography', name: 'AboutGeography', component: () => import('../pages/about/AboutManagementPage.vue'), props: { section: 'geography' }, meta: { title: '城市与区域管理', requiresAuth: true, permissions: ['data_scope_config:list'] } },
      { path: 'about/permissions', name: 'AboutPermissions', component: () => import('../pages/about/AboutManagementPage.vue'), props: { section: 'permissions' }, meta: { title: '角色与权限管理', requiresAuth: true, permissions: ['role_permission_code:list', 'data_scope_config:list'] } },
      { path: 'about/operating-governance', name: 'OperatingGovernance', component: () => import('../pages/about/OperatingGovernancePage.vue'), meta: { title: '运营主体管理', requiresAuth: true, permissions: ['operating_governance:list', 'sub_operator:list'] } },
      { path: 'about/sub-operators', name: 'SubOperatorList', redirect: to => ({ name: 'OperatingGovernance', query: { ...to.query, tab: 'entities', entityTab: 'partners' }, hash: to.hash }) },
      // —— 统计分析 ——
      { path: 'analytics', redirect: '/analytics/gmv' },
      { path: 'analytics/gmv', name: 'GmvAnalytics', component: () => import('../views/analytics/gmv/GmvAnalyticsView.vue'), meta: { title: 'GMV统计', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      { path: 'analytics/orders', name: 'OrdersAnalytics', component: () => import('../views/analytics/orders/OrdersAnalyticsView.vue'), meta: { title: '订单统计', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      { path: 'analytics/delivery', name: 'DeliveryAnalytics', component: () => import('../views/analytics/delivery/DeliveryAnalyticsView.vue'), meta: { title: '配送统计', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      { path: 'analytics/alerts', name: 'AlertsAnalytics', component: () => import('../views/analytics/alerts/AlertsAnalyticsView.vue'), meta: { title: '工单预警', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      { path: 'analytics/cabinet', name: 'CabinetAnalytics', component: () => import('../views/analytics/cabinet/CabinetAnalyticsView.vue'), meta: { title: '餐柜统计', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      { path: 'analytics/activity', name: 'ActivityAnalytics', component: () => import('../views/analytics/activity/ActivityAnalyticsView.vue'), meta: { title: '用户活跃度', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      // M8.2: 财务分析
      { path: 'analytics/finance', name: 'FinanceAnalytics', component: () => import('../views/analytics/finance/FinanceAnalyticsView.vue'), meta: { title: '财务分析', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      // M8.2: 合同资质分析
      { path: 'analytics/contract-qualification', name: 'ContractQualificationAnalytics', component: () => import('../views/analytics/contract-qualification/ContractQualificationAnalyticsView.vue'), meta: { title: '合同资质分析', menuId: 'analytics', requiresAuth: true, permissions: ['analytics:view'] } },
      // —— 帮助中心 ——
      { 
        path: 'help', 
        name: 'HelpCenter', 
        component: () => import('../views/help/HelpCenterPage.vue'), 
        meta: { 
          title: '帮助中心', 
          requiresAuth: true,
          menuId: 'help',
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '帮助中心' }]
        } 
      },
      { 
        path: 'help/manuals/:slug', 
        name: 'ManualDetail', 
        component: () => import('../views/help/ManualDetailPage.vue'), 
        meta: { 
          title: '使用手册', 
          requiresAuth: true,
          menuId: 'help',
          breadcrumbs: [{ title: '首页', path: '/' }, { title: '帮助中心', path: '/help' }, { title: '使用手册' }]
        } 
      },
      // —— 用户中心（所有登录用户可见，无权限守卫） ——
      { path: 'user-center/profile', name: 'UserProfile', component: () => import('../pages/user-center/UserProfilePage.vue'), meta: { title: '个人中心', requiresAuth: true, menuId: 'user-center', breadcrumbs: [{ title: '首页', path: '/' }, { title: '用户中心' }, { title: '个人中心' }] } },
      { path: 'user-center/my-position', redirect: '/user-center/profile' },
      { path: 'user-center/change-password', redirect: '/user-center/profile' },
      { path: 'user-center/permissions', name: 'MyPermissions', component: () => import('../pages/user-center/MyPermissionsPage.vue'), meta: { title: '权限概览', requiresAuth: true, menuId: 'user-center', breadcrumbs: [{ title: '首页', path: '/' }, { title: '用户中心' }, { title: '权限概览' }] } },
    ]
  },
  {
    path: '/access-guidance',
    name: 'AccessGuidance',
    component: () => import('../views/error/AccessGuidanceView.vue'),
    meta: {
      title: '访问指引',
      requiresAuth: false
    }
  },
  {
    path: '/403',
    name: 'Forbidden',
    component: () => import('../views/error/ForbiddenView.vue'),
    meta: {
      title: '无权访问',
      requiresAuth: false
    }
  },
  {
    path: '/404',
    name: 'NotFound',
    component: () => import('../views/error/NotFoundView.vue'),
    meta: {
      title: '页面不存在',
      requiresAuth: false
    }
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/404'
  }
]

// 创建路由实例
const router = createRouter({
  history: createWebHistory('/operator-admin/'),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else {
      return { top: 0 }
    }
  }
})

// 设置路由守卫（限制本应用仅允许运营商/超管）
setupAuthGuards(router, { loginPath: '/login', homePath: '/', requireRoles: ['operator'], guidancePath: '/access-guidance' })

// ⭐ 设置 chunk 加载失败自动恢复（部署新版本后，旧 chunk 404 时自动刷新）
setupChunkErrorHandler(router)

export default router
